import React, { useCallback } from 'react'
import { Box, Paper, Stack, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TooltipProps } from 'recharts'
import { getLocalizedNumber } from '../../../shared/utils/localized-number'
import { MetricType } from 'src/Libs/e-mobility/dashboard/models/dashboard.model'
import { getCurrencyValue } from 'src/Libs/e-mobility/shared'
import { UnitTypes } from 'src/Libs/types'

const useStyles = makeStyles()(theme => ({
  tooltipIndicator: {
    width: 8,
    height: 8,
    alignSelf: 'center',
  },
  tooltipIndicatorGrid: {
    backgroundColor: theme.palette.chart.totalGridChargedBar,
  },
  tooltipIndicatorSolar: {
    backgroundColor: theme.palette.chart.solarChargedBar,
  },
  tooltipIndicatorDischarge: {
    backgroundColor: theme.palette.chart.totalDischargedEnergyBackground,
  },
}))

export const ChargingSessionChartTooltip: React.FC<TooltipProps<number, string> & { currencyCode: string; metric: MetricType }> = ({
  active,
  payload,
  label: date,
  currencyCode,
  metric,
}): React.ReactElement | null => {
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()

  const formatValue = useCallback((value: number | undefined): string => {
    return `${value ? getLocalizedNumber(value) : 0} ${UnitTypes.kiloWattHour}`
  }, [])

  if (!payload || payload.length === 0 || !active) {
    return null
  }

  /* Payload is something that comes from recharts library
   * It is built in native Tooltip component, in TS as TooltipProps
   * Payload always comes as array, so all tooltip items must be taken from array
   * For more information, please visit https://recharts.org/en-US/api/Tooltip
   */
  const gridChargedTooltip = formatValue(payload.find(item => item.dataKey === 'grid')?.value)
  const solarChargedTooltip = formatValue(payload.find(item => item.dataKey === 'solar')?.value)
  const dischargedTooltip = formatValue(Math.abs(payload.find(item => item.dataKey === 'discharged')?.value || 0))
  const gridCostTooltip = getCurrencyValue(payload.find(item => item.dataKey === 'gridCost')?.value || 0, currencyCode)
  const solarCostTooltip = getCurrencyValue(payload.find(item => item.dataKey === 'solarCost')?.value || 0, currencyCode)
  const dischargeCostTooltip = getCurrencyValue(Math.abs(payload.find(item => item.dataKey === 'dischargeCost')?.value || 0), currencyCode)

  const renderEnergyTooltip = (): React.ReactElement => (
    <>
      <Stack direction="row" spacing={1}>
        <Box className={cx(classes.tooltipIndicator, classes.tooltipIndicatorGrid)} />
        <Typography variant="caption">{gridChargedTooltip}</Typography>
      </Stack>

      <Stack direction="row" spacing={1}>
        <Box className={cx(classes.tooltipIndicator, classes.tooltipIndicatorSolar)} />
        <Typography variant="caption">{solarChargedTooltip}</Typography>
      </Stack>

      {dischargedTooltip && (
        <Stack direction="row" spacing={1}>
          <Box className={cx(classes.tooltipIndicator, classes.tooltipIndicatorDischarge)} />
          <Typography variant="caption">{dischargedTooltip}</Typography>
        </Stack>
      )}
    </>
  )

  const renderCostTooltip = (): React.ReactElement => (
    <>
      <Stack direction="row" spacing={1}>
        <Box className={cx(classes.tooltipIndicator, classes.tooltipIndicatorGrid)} />
        <Typography variant="caption">{gridCostTooltip}</Typography>
      </Stack>

      <Stack direction="row" spacing={1}>
        <Box className={cx(classes.tooltipIndicator, classes.tooltipIndicatorSolar)} />
        <Typography variant="caption">{solarCostTooltip}</Typography>
      </Stack>

      {dischargeCostTooltip && (
        <Stack direction="row" spacing={1}>
          <Box className={cx(classes.tooltipIndicator, classes.tooltipIndicatorDischarge)} />
          <Typography variant="caption">{dischargeCostTooltip}</Typography>
        </Stack>
      )}
    </>
  )

  const renderContent = (): React.ReactElement => {
    switch (metric) {
      case MetricType.Cost:
        return renderCostTooltip()
      case MetricType.Energy:
        return renderEnergyTooltip()
    }
  }

  return (
    <Paper sx={theme => ({ borderRadius: 2, paddingTop: theme.spacing(1), minWidth: 90 })} variant="outlined">
      <Typography variant="overline">{date || t('common.noDataShort')}</Typography>
      {renderContent()}
    </Paper>
  )
}
